import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  output,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { isDefined, isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { CameraProjection } from '../../connect-3d-ext/camera/camera.models';
import { ExternalFileId } from '../../connect/models/external-file-id';
import {
  PointcloudStatus,
  PointcloudType,
  ScandataDisplayStatus,
  ScandataLoadStatus,
  ScandataModel,
  ScandataSaveStatus,
} from '../../scandata/scandata.models';
import { calculateStationHeadingOffset } from '../../station/station-transform.models';
import {
  CurrentStation,
  Station,
  StationDisplayStatus,
  StationLoadStatus,
} from '../../station/station.models';
import { STATION_PAGE_SIZE_LIMIT } from '../../station/station.service';
import {
  ScandataTreeIconComponent,
  TreeIcon,
} from '../scandata-tree-icon/scandata-tree-icon.component';
import { ScandataTreeMenuComponent } from '../scandata-tree-menu/scandata-tree-menu.component';
import { scandataTreeTranslations } from './scandata-tree.translations';

interface ScanView {
  scan: ScandataModel;
  version: string | null;
  fitToViewDisabled: boolean;
  rotatePanoramasDisabled: boolean;
  stationHeadingOffsetDegrees: number;
  hasViewInNumarkFeature: boolean;
  loadDisabled: boolean;
  icon: TreeIcon;
  notReady: boolean;
  hasError: boolean;
  infoMessage?: string;

  stationsLoading: boolean;
  stationsNotLoaded: boolean;
  stations?: StationView[];
}

interface StationView {
  station: Station;
  icon: TreeIcon;
  hasError: boolean;
  errorMessage?: string;
  tooltip: string;
  displayName: string;
}

@Component({
  selector: 'sd-scandata-tree',
  imports: [
    CommonModule,
    ModusIconModule,
    ModusButtonModule,
    ModusTooltipModule,
    MatProgressSpinnerModule,
    ScandataTreeIconComponent,
    ScandataTreeMenuComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scandata-tree.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScandataTreeComponent {
  private readonly translationService = inject(TranslationService);

  readonly data = input.required<ScandataModel[]>();

  readonly currentStation = input<CurrentStation>();
  readonly cameraProjection = input.required<CameraProjection>();
  readonly quotaExceeded = input(false);
  readonly hasRotatePanoramasFeature = input(false);
  readonly hasViewInNumarkFeature = input(false);

  readonly modelIconClick = output<ScandataModel>();
  readonly modelNameClick = output<ScandataModel>();
  readonly modelDetailClick = output<ScandataModel>();
  readonly modelExpandClick = output<ScandataModel>();
  readonly modelFitToViewClick = output<ScandataModel>();
  readonly modelRotatePanoramasClick = output<ScandataModel>();

  readonly stationIconClick = output<Station>();
  readonly stationNameClick = output<Station>();
  readonly stationMoreClick = output<Station>();
  readonly viewInNumarkClick = output<ScandataModel>();

  readonly translations = scandataTreeTranslations();

  readonly stationsNotLoadedMessage = this.translationService.translate(
    this.translations.station.notLoaded.text,
    { limit: STATION_PAGE_SIZE_LIMIT },
  );

  readonly stationDisabledReason = computed(() =>
    this.cameraProjection() === CameraProjection.Orthographic
      ? this.translations.station.disabled.text
      : '',
  );

  readonly stationTooltipDisabled = computed(
    () => this.cameraProjection() !== CameraProjection.Orthographic,
  );

  readonly loadStationDisabled = computed(() => {
    return (
      this.currentStation()?.displayStatus === StationDisplayStatus.AwaitingDisplay ||
      this.cameraProjection() === CameraProjection.Orthographic ||
      this.quotaExceeded()
    );
  });

  // Compounded Signal
  readonly scanViewDataParams = computed(() => {
    return {
      scans: this.data(),
      currentStation: this.currentStation(),
      quotaExceeded: this.quotaExceeded(),
    };
  });

  readonly scanViewData = computed<ScanView[]>(() => {
    const { scans, quotaExceeded, currentStation } = this.scanViewDataParams();
    return this.mapToScanViews(scans, quotaExceeded, currentStation);
  });

  modelIconClicked(model: ScandataModel) {
    this.modelIconClick.emit(model);
  }

  modelNameClicked(model: ScandataModel) {
    this.modelNameClick.emit(model);
  }

  modelDetailClicked(model: ScandataModel) {
    this.modelDetailClick.emit(model);
  }

  modelFitToViewClicked(model: ScandataModel) {
    this.modelFitToViewClick.emit(model);
  }

  modelRotatePanoramasClicked(model: ScandataModel) {
    this.modelRotatePanoramasClick.emit(model);
  }

  stationIconClicked(station: Station) {
    this.stationIconClick.emit(station);
  }

  stationNameClicked(station: Station) {
    if (this.loadStationDisabled()) return;

    this.stationNameClick.emit(station);
  }

  stationMoreClicked(station: Station) {
    this.stationMoreClick.emit(station);
  }

  toggleExpand(model: ScandataModel) {
    this.modelExpandClick.emit(model);
  }

  modelViewInNumarkClicked(model: ScandataModel) {
    this.viewInNumarkClick.emit(model);
  }

  private mapToScanViews(
    scans: ScandataModel[],
    quotaExceeded: boolean,
    currentStation?: CurrentStation,
  ): ScanView[] {
    return scans.map((scan) => {
      const fitToViewDisabled =
        scan.displayStatus !== ScandataDisplayStatus.Displayed || isDefined(currentStation);

      const rotatePanoramasDisabled =
        scan.numberOfStations === 0 || scan.saveStatus === ScandataSaveStatus.Saving;

      const stationHeadingOffsetDegrees = calculateStationHeadingOffset(scan.stationTransform);

      const hasViewInNumarkFeature =
        this.hasViewInNumarkFeature() && scan.type === PointcloudType.MobileMappingRun;

      const loadDisabled =
        scan.pointCount === 0 || scan.pointcloudStatus !== PointcloudStatus.Ready || quotaExceeded;

      const notReady =
        scan.pointcloudStatus === PointcloudStatus.Uploading ||
        scan.pointcloudStatus === PointcloudStatus.Processing;

      const icon = this.getModelIcon(scan, notReady, quotaExceeded);

      const hasError = icon === TreeIcon.Error || scan.saveStatus === ScandataSaveStatus.SaveError;

      const infoMessage = this.getModelInfoMessage(scan, notReady);

      const stationsLoading =
        !scan.stations && scan.stationLoadStatus === StationLoadStatus.Loading;

      const stationsNotLoaded =
        scan.stationLoadStatus === StationLoadStatus.Loaded &&
        scan.numberOfStations > STATION_PAGE_SIZE_LIMIT;

      return {
        scan,
        version: new ExternalFileId(scan.externalFileId).revisionLabel,
        fitToViewDisabled,
        rotatePanoramasDisabled,
        stationHeadingOffsetDegrees,
        hasViewInNumarkFeature,
        loadDisabled,
        icon,
        notReady,
        hasError,
        infoMessage,

        stationsLoading,
        stationsNotLoaded,
        stations: isNil(scan.stations)
          ? undefined
          : this.mapToStationViews(scan.stations, quotaExceeded, currentStation),
      } satisfies ScanView;
    });
  }

  private mapToStationViews(
    stations: Station[],
    quotaExceeded: boolean,
    currentStation?: CurrentStation,
  ): StationView[] {
    return stations.map((station) => {
      const icon = this.getStationIcon(station, quotaExceeded, currentStation);

      const hasError = icon === TreeIcon.Error;

      const errorMessage = hasError ? this.translations.station.loadError.text : undefined;

      const tooltip = this.translationService.translate(this.translations.station.tooltip.text, {
        stationName: station.name,
      });

      const displayName = this.translationService.translate(
        this.translations.station.displayName.text,
        { stationName: station.name },
      );

      return {
        station,
        icon,
        hasError,
        errorMessage,
        tooltip,
        displayName,
      } satisfies StationView;
    });
  }

  private getModelInfoMessage(model: ScandataModel, notReady: boolean): string | undefined {
    if (notReady) {
      return this.translations.scanInfoMessage.notReady.text;
    }

    if (model.saveStatus === ScandataSaveStatus.SaveError) {
      return this.translations.scanInfoMessage.saveError.text;
    }

    if (model.pointcloudStatus === PointcloudStatus.Failed) {
      return this.translations.scanInfoMessage.failedToIngest.text;
    }

    if (
      model.loadStatus === ScandataLoadStatus.LoadingError ||
      model.displayStatus === ScandataDisplayStatus.DisplayError
    ) {
      return this.translations.scanInfoMessage.failedToLoad.text;
    }

    if (model.pointCount === 0) {
      return this.translations.scanInfoMessage.stationsOnly.text;
    }

    return;
  }

  private getModelIcon(model: ScandataModel, notReady: boolean, quotaExceeded: boolean): TreeIcon {
    if (notReady || quotaExceeded) {
      return TreeIcon.Unavailable;
    }

    if (model.pointcloudStatus === PointcloudStatus.Failed) {
      return TreeIcon.Error;
    }

    if (
      model.loadStatus === ScandataLoadStatus.LoadingError ||
      model.displayStatus === ScandataDisplayStatus.DisplayError
    ) {
      return TreeIcon.Error;
    }

    if (
      model.loadStatus === ScandataLoadStatus.Loading ||
      model.displayStatus === ScandataDisplayStatus.AwaitingDisplay
    ) {
      return TreeIcon.Loading;
    }

    if (!model.showInScene) {
      return TreeIcon.Off;
    }

    return TreeIcon.On;
  }

  private getStationIcon(
    station: Station,
    quotaExceeded: boolean,
    currentStation?: CurrentStation,
  ): TreeIcon {
    if (quotaExceeded) {
      return TreeIcon.Unavailable;
    }

    const viewed = station.id === currentStation?.station.id;
    if (viewed) {
      if (currentStation.displayStatus === StationDisplayStatus.AwaitingDisplay) {
        return TreeIcon.Loading;
      }

      if (currentStation.displayStatus === StationDisplayStatus.DisplayError) {
        return TreeIcon.Error;
      }

      if (currentStation.displayStatus === StationDisplayStatus.Displayed) {
        return TreeIcon.On;
      }

      return TreeIcon.Off;
    } else {
      return TreeIcon.Off;
    }
  }
}
